/* A church awaiting its admin — what it is waiting for, and what can be done.
 *
 * The badge said "Awaiting admin" and nothing on the page said why, or what
 * the Super Admin could do about it. There are three answers:
 *
 *   the invited admin has asked     approve them here, not only in Requests
 *   they signed up the other way    their account exists but never used the
 *                                   link — make them the admin here
 *   nobody has signed up yet        share the signup link; nothing to approve
 *
 * Either action goes through the same approval as the Requests queue, so the
 * person is promoted, moved out of any other church, and told.
 */
import { useState } from 'react'
import { Hourglass } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { ApiError } from '@/api/client'
import { useAdminStatus, useAppointAdmin } from '@/api/console'

export function AwaitingAdmin({
  churchId,
  churchName,
  contact,
}: {
  churchId: string
  churchName: string
  /** The mobile or email the church was created for. */
  contact: string | null
}) {
  const status = useAdminStatus(churchId, true)
  const appoint = useAppointAdmin(churchId)
  const [confirming, setConfirming] = useState(false)

  const s = status.data
  const who = s?.pending_request ?? s?.invited_account ?? null
  const whoLine = who
    ? [who.name, who.mobile, who.email].filter(Boolean).join(' · ')
    : ''

  let heading: string
  let detail: string
  if (s?.pending_request) {
    heading = `${s.pending_request.name || 'The invited admin'} has asked to run ${churchName}`
    detail = 'They signed up through the signup link. Approve them to make the church active.'
  } else if (s?.invited_account) {
    heading = `${s.invited_account.name || 'The invited admin'} has an account, but has not used the signup link`
    detail = s.invited_account.in_another_church
      ? 'They are a member of another church. Making them the admin moves them here.'
      : 'You can make them the admin now; the church becomes active straight away.'
  } else {
    heading = 'Waiting for the admin to sign up'
    detail = `Nobody has signed up with ${contact ?? 'the admin’s contact'} yet. Copy the signup link above and send it to them. Once they sign up, you can approve them here.`
  }

  return (
    <Card className="border-line-2 bg-warning-soft p-s4">
      <div className="flex flex-wrap items-start gap-s3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-surface text-ink-2">
          <Hourglass className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-md font-bold">{status.isLoading ? 'Checking…' : heading}</h2>
          {whoLine ? <p className="text-sm text-ink-2">{whoLine}</p> : null}
          {status.isLoading ? null : (
            <p className="mt-1 text-base text-ink-2">{detail}</p>
          )}
          {appoint.isError ? (
            <p role="alert" className="mt-s2 text-base text-danger">
              {appoint.error instanceof ApiError
                ? appoint.error.message
                : 'Could not make them the admin. Please try again.'}
            </p>
          ) : null}
        </div>
        {who ? (
          <Button variant="primary" onClick={() => setConfirming(true)}>
            {s?.pending_request ? 'Approve' : 'Make admin'}
          </Button>
        ) : null}
      </div>

      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title={`Make ${who?.name || 'them'} the admin of ${churchName}?`}
        body={`${churchName} becomes active, and they can run it from the app straight away. They are told by notification.`}
        confirmLabel={s?.pending_request ? 'Approve' : 'Make admin'}
        tone="primary"
        loading={appoint.isPending}
        onConfirm={() =>
          appoint.mutate(undefined, { onSettled: () => setConfirming(false) })
        }
      />
    </Card>
  )
}
